import { clampUnitInterval } from './EngagementScore';

export type MuteState = 'muted' | 'unmuted' | 'unknown';

export interface VisibilitySnapshot {
  readonly supported: boolean;
  readonly documentVisible?: boolean;
  readonly elementVisibilityRatio?: number;
  readonly hasValidSample: boolean;
  readonly detached: boolean;
  readonly invalid: boolean;
}

export interface EngagementObservation {
  readonly isSeeking: boolean;
  readonly isBuffering: boolean;
  readonly muteState: MuteState;
  readonly visibility: VisibilitySnapshot;
}

export interface EngagementBehaviorAggregate {
  readonly activePlaybackDuration: number;
  readonly mutedActivePlaybackDuration: number;
  readonly hasUnknownMuteState: boolean;
  readonly visibilitySupported: boolean;
  readonly visibilityWeightedActiveDuration: number;
  readonly visibilityHasValidSample: boolean;
  readonly visibilityMissingSampleDuringActivePlayback: boolean;
  readonly visibilityInvalid: boolean;
  readonly visibilityDetached: boolean;
}

interface EngagementBehaviorTrackerOptions {
  readonly getObservation: () => EngagementObservation;
}

interface MutableEngagementBehaviorAggregate {
  activePlaybackDuration: number;
  mutedActivePlaybackDuration: number;
  hasUnknownMuteState: boolean;
  visibilitySupported: boolean;
  visibilityWeightedActiveDuration: number;
  visibilityHasValidSample: boolean;
  visibilityMissingSampleDuringActivePlayback: boolean;
  visibilityInvalid: boolean;
  visibilityDetached: boolean;
}

const EMPTY_AGGREGATE: EngagementBehaviorAggregate = Object.freeze({
  activePlaybackDuration: 0,
  mutedActivePlaybackDuration: 0,
  hasUnknownMuteState: false,
  visibilitySupported: false,
  visibilityWeightedActiveDuration: 0,
  visibilityHasValidSample: false,
  visibilityMissingSampleDuringActivePlayback: false,
  visibilityInvalid: false,
  visibilityDetached: false,
});

export function createEngagementBehaviorTracker({ getObservation }: EngagementBehaviorTrackerOptions) {
  let aggregate = createEmptyMutableAggregate();

  return {
    observe(playTimeDelta: number): void {
      if (!Number.isFinite(playTimeDelta) || playTimeDelta <= 0) {
        return;
      }

      const observation = getObservation();
      if (observation.isSeeking || observation.isBuffering) {
        return;
      }

      aggregate.activePlaybackDuration += playTimeDelta;

      if (observation.muteState === 'unknown') {
        aggregate.hasUnknownMuteState = true;
      } else if (observation.muteState === 'muted') {
        aggregate.mutedActivePlaybackDuration += playTimeDelta;
      }

      const visibility = observation.visibility;
      if (!visibility.supported) {
        return;
      }

      aggregate.visibilitySupported = true;
      if (visibility.detached) {
        aggregate.visibilityDetached = true;
        return;
      }

      if (visibility.invalid) {
        aggregate.visibilityInvalid = true;
        return;
      }

      if (!visibility.hasValidSample) {
        aggregate.visibilityMissingSampleDuringActivePlayback = true;
        return;
      }

      const elementVisibilityRatio = clampUnitInterval(visibility.elementVisibilityRatio);
      if (elementVisibilityRatio === undefined) {
        aggregate.visibilityInvalid = true;
        return;
      }

      aggregate.visibilityHasValidSample = true;
      const documentVisibilityWeight = visibility.documentVisible === false ? 0 : 1;
      aggregate.visibilityWeightedActiveDuration += playTimeDelta * documentVisibilityWeight * elementVisibilityRatio;
    },

    snapshot(): EngagementBehaviorAggregate {
      return Object.freeze({ ...aggregate });
    },

    reset(): void {
      aggregate = createEmptyMutableAggregate();
    },
  };
}

export function deriveUnmutedActiveRatio(aggregate: EngagementBehaviorAggregate): {
  unmutedActiveRatio?: number;
  mutedWatchDuration?: number;
  activePlaybackDuration?: number;
} {
  const activePlaybackDuration = aggregate.activePlaybackDuration;
  if (!Number.isFinite(activePlaybackDuration) || activePlaybackDuration <= 0 || aggregate.hasUnknownMuteState) {
    return {};
  }

  const mutedWatchDuration = Math.min(Math.max(aggregate.mutedActivePlaybackDuration, 0), activePlaybackDuration);
  const unmutedActiveRatio = clampUnitInterval((activePlaybackDuration - mutedWatchDuration) / activePlaybackDuration);
  if (unmutedActiveRatio === undefined) {
    return {};
  }

  return {
    unmutedActiveRatio,
    mutedWatchDuration,
    activePlaybackDuration,
  };
}

export function deriveVisibilityRatio(aggregate: EngagementBehaviorAggregate): number | undefined {
  if (!aggregate.visibilitySupported || aggregate.visibilityInvalid || aggregate.visibilityDetached) {
    return undefined;
  }

  if (aggregate.visibilityMissingSampleDuringActivePlayback || !aggregate.visibilityHasValidSample) {
    return undefined;
  }

  if (!Number.isFinite(aggregate.activePlaybackDuration) || aggregate.activePlaybackDuration <= 0) {
    return undefined;
  }

  return clampUnitInterval(aggregate.visibilityWeightedActiveDuration / aggregate.activePlaybackDuration);
}

function createEmptyMutableAggregate(): MutableEngagementBehaviorAggregate {
  return {
    activePlaybackDuration: 0,
    mutedActivePlaybackDuration: 0,
    hasUnknownMuteState: false,
    visibilitySupported: false,
    visibilityWeightedActiveDuration: 0,
    visibilityHasValidSample: false,
    visibilityMissingSampleDuringActivePlayback: false,
    visibilityInvalid: false,
    visibilityDetached: false,
  };
}

export { EMPTY_AGGREGATE };
